import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ChevronRight,
  User,
} from "lucide-react";

// Google icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function RegistroAcceso() {
  const navigate = useNavigate();
  const [method, setMethod] = useState(null); // null, 'google', 'email'
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  const handleGoogleClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("google_proposito", "registro");
    const redirectUrl = window.location.origin + "/registro-vendedor/acceso";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailClick = () => {
    setMethod('email');
  };

  const validateEmail = () => {
    const newErrors = {};
    if (!form.email.trim()) {
      newErrors.email = "Email requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Email inválido";
    }
    if (!form.password) {
      newErrors.password = "Contraseña requerida";
    } else if (form.password.length < 6) {
      newErrors.password = "Mínimo 6 caracteres";
    }
    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (method === 'email') {
      if (validateEmail()) {
        sessionStorage.setItem('registro_method', 'email');
        sessionStorage.setItem('registro_email', form.email);
        sessionStorage.setItem('registro_password', form.password);
        navigate("/registro-vendedor/tipo");
      }
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#4A2C0A] font-bold">
          Crea tu cuenta
        </h1>
        <p className="font-['Poppins'] text-[#4A2C0A]/70">
          Elige cómo quieres registrarte
        </p>
      </div>

      {/* Method Selection */}
      {!method && (
        <div className="space-y-4">
          {/* Google Option - Primary */}
          <Button
            onClick={handleGoogleClick}
            variant="outline"
            className="w-full h-14 text-base bg-white hover:bg-gray-50 border-2 border-gray-200 rounded-2xl font-['Poppins'] flex items-center justify-center gap-3"
            data-testid="btn-google-signup"
          >
            <GoogleIcon />
            <span className="font-medium text-gray-700">Continuar con Google</span>
          </Button>

          {/* Divider */}
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E5D4B3]"></div>
            </div>
            <span className="relative bg-[#FBF5E8] px-4 text-sm text-[#4A2C0A]/50 font-['Poppins']">
              o
            </span>
          </div>

          {/* Email Option */}
          <Button
            onClick={handleEmailClick}
            variant="outline"
            className="w-full h-14 text-base bg-white/50 hover:bg-white border-2 border-[#E5D4B3] rounded-2xl font-['Poppins'] flex items-center justify-center gap-3"
            data-testid="btn-email-signup"
          >
            <Mail className="w-5 h-5 text-[#C8902A]" />
            <span className="font-medium text-[#4A2C0A]">Registrarme con email</span>
          </Button>

          {/* Info */}
          <p className="text-center text-xs text-[#4A2C0A]/50 font-['Poppins'] mt-4">
            Si usas Google, tu nombre y foto se importarán automáticamente
          </p>
        </div>
      )}

      {/* Email Form */}
      {method === 'email' && (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl p-6 space-y-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[#C8902A]/10 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#C8902A]" />
              </div>
              <div>
                <h3 className="font-['Poppins'] font-medium text-[#4A2C0A]">
                  Registro con email
                </h3>
                <p className="text-xs text-[#4A2C0A]/60 font-['Poppins']">
                  Crea tu cuenta con email y contraseña
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Email */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#4A2C0A]/80">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C0A]/40" />
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={`pl-10 h-12 rounded-xl border-[#E5D4B3] focus:border-[#C8902A] focus:ring-[#C8902A]/20 ${
                      errors.email ? "border-red-400" : ""
                    }`}
                    data-testid="input-email"
                  />
                </div>
                {errors.email && (
                  <p className="text-xs text-red-500 font-['Poppins']">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#4A2C0A]/80">Contraseña *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C0A]/40" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Mínimo 6 caracteres"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className={`pl-10 pr-10 h-12 rounded-xl border-[#E5D4B3] focus:border-[#C8902A] focus:ring-[#C8902A]/20 ${
                      errors.password ? "border-red-400" : ""
                    }`}
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A2C0A]/40 hover:text-[#4A2C0A]/60"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-500 font-['Poppins']">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#4A2C0A]/80">Confirmar contraseña *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C0A]/40" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Repite tu contraseña"
                    value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    className={`pl-10 h-12 rounded-xl border-[#E5D4B3] focus:border-[#C8902A] focus:ring-[#C8902A]/20 ${
                      errors.confirmPassword ? "border-red-400" : ""
                    }`}
                    data-testid="input-confirm-password"
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-xs text-red-500 font-['Poppins']">{errors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setMethod(null)}
              className="flex-1 h-12 rounded-xl border-[#E5D4B3]"
            >
              Volver
            </Button>
            <Button
              onClick={handleContinue}
              className="flex-1 h-12 bg-[#C8902A] hover:bg-[#A67720] rounded-xl"
              data-testid="btn-continue-email"
            >
              Continuar
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
